import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Calendar as CalIcon, Check, X, Trash2, Plus, ExternalLink, AlertCircle,
  Facebook, Instagram, Target, BadgeDollarSign, Code, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import UsersTab from "@/components/UsersTab";

export default function SettingsPage() {
  const [searchParams] = useSearchParams();
  const googleResult = searchParams.get("google");
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "super_admin";

  // Senders
  const [senders, setSenders] = useState([]);
  const [senderForm, setSenderForm] = useState({ name: "", email: "", reply_to: "", is_default: false });

  // Google Calendar
  const [gcfg, setGcfg] = useState({ client_id: "", client_secret: "", redirect_uri: "" });
  const [gstatus, setGstatus] = useState(null);
  const [gOpen, setGOpen] = useState(false);

  // Meta Social (Page + IG)
  const [metaSocial, setMetaSocial] = useState({ configured: false });
  const [metaSocialForm, setMetaSocialForm] = useState({ page_id: "", page_access_token: "", ig_user_id: "" });
  const [metaSocialOpen, setMetaSocialOpen] = useState(false);

  // Meta Ads
  const [metaAds, setMetaAds] = useState({ configured: false });
  const [metaAdsForm, setMetaAdsForm] = useState({ app_id: "", app_secret: "", access_token: "", ad_account_id: "", business_id: "" });
  const [metaAdsOpen, setMetaAdsOpen] = useState(false);

  // Google Ads
  const [googleAds, setGoogleAds] = useState({ configured: false });
  const [googleAdsForm, setGoogleAdsForm] = useState({ developer_token: "", client_id: "", client_secret: "", refresh_token: "", customer_id: "", login_customer_id: "" });
  const [googleAdsOpen, setGoogleAdsOpen] = useState(false);

  // Widget embed code
  const [widget, setWidget] = useState({});
  const [widgetForm, setWidgetForm] = useState({ cta_label: "Plan your trip", primary_color: "#D95D39", dark_color: "#0A1128" });
  const [newAgent, setNewAgent] = useState({ name: "", photo_url: "" });
  const widgetUrl = `${process.env.REACT_APP_BACKEND_URL}/widget.js`;
  const embedSnippet = `<div id="espiritu-chat-widget"></div>\n<script src="${widgetUrl}" async></script>`;
  const copyEmbed = () => {
    navigator.clipboard.writeText(embedSnippet);
    toast.success("Snippet copiado");
  };
  const saveWidget = async () => {
    try {
      await api.patch("/settings/widget/config", widgetForm);
      toast.success("Diseño del widget guardado");
      load();
    } catch (e) {
      toast.error("No se pudo guardar el diseño");
    }
  };
  const addAgent = async () => {
    if (!newAgent.name || !newAgent.photo_url) return toast.error("Nombre y URL de foto requeridos");
    try {
      await api.post("/settings/widget/agents", newAgent);
      setNewAgent({ name: "", photo_url: "" });
      toast.success("Travel agent añadido");
      load();
    } catch {
      toast.error("No se pudo añadir el agente");
    }
  };
  const delAgent = async (id) => {
    if (!window.confirm("¿Borrar este travel agent?")) return;
    try {
      await api.delete(`/settings/widget/agents/${id}`);
      toast.success("Agente eliminado");
      load();
    } catch {
      toast.error("No se pudo borrar");
    }
  };

  // Mailchimp
  const [mailchimp, setMailchimp] = useState({ configured: false });
  const [mailchimpAudiences, setMailchimpAudiences] = useState([]);
  const [mailchimpImporting, setMailchimpImporting] = useState(false);

  const load = () => {
    api.get("/settings/senders").then((r) => setSenders(r.data));
    api.get("/settings/google/config").then((r) => setGcfg({
      client_id: r.data.client_id || "",
      client_secret: "",
      redirect_uri: r.data.redirect_uri || `${process.env.REACT_APP_BACKEND_URL}/api/calendar/google/callback`,
    }));
    api.get("/settings/google/status").then((r) => setGstatus(r.data));
    api.get("/settings/meta-social/config").then((r) => setMetaSocial(r.data));
    api.get("/settings/meta-ads/config").then((r) => setMetaAds(r.data));
    api.get("/settings/google-ads/config").then((r) => setGoogleAds(r.data));
    api.get("/settings/widget/config").then((r) => {
      setWidget(r.data);
      setWidgetForm({
        cta_label: r.data.cta_label || "Plan your trip",
        primary_color: r.data.primary_color || "#D95D39",
        dark_color: r.data.dark_color || "#0A1128",
      });
    });
    api.get("/mailchimp/status").then((r) => setMailchimp(r.data)).catch(() => {});
    api.get("/mailchimp/audiences").then((r) => setMailchimpAudiences(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (googleResult === "connected") toast.success("Google Calendar conectado correctamente");
    if (googleResult === "error") toast.error("Error conectando con Google Calendar");
  }, [googleResult]);

  // ---- Sender actions ----
  const addSender = async () => {
    if (!senderForm.name || !senderForm.email) return toast.error("Nombre y email obligatorios");
    await api.post("/settings/senders", { ...senderForm, reply_to: senderForm.reply_to || null });
    setSenderForm({ name: "", email: "", reply_to: "", is_default: false });
    toast.success("Remitente añadido");
    load();
  };
  const setDefault = async (s) => {
    await api.patch(`/settings/senders/${s.id}`, { ...s, is_default: true });
    toast.success("Remitente por defecto actualizado");
    load();
  };
  const delSender = async (id) => {
    await api.delete(`/settings/senders/${id}`);
    load();
  };

  // ---- Google Calendar actions ----
  const saveGoogleConfig = async () => {
    if (!gcfg.client_id || !gcfg.client_secret || !gcfg.redirect_uri) {
      return toast.error("Completa todos los campos");
    }
    await api.post("/settings/google/config", gcfg);
    toast.success("Configuración Google guardada");
    setGOpen(false);
    load();
  };
  const connectGoogle = async () => {
    try {
      const r = await api.get("/settings/google/auth-url");
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error iniciando OAuth");
    }
  };
  const disconnectGoogle = async () => {
    await api.post("/settings/google/disconnect");
    toast.success("Google Calendar desconectado");
    load();
  };

  // ---- Meta Social ----
  const saveMetaSocial = async () => {
    if (!metaSocialForm.page_id || !metaSocialForm.page_access_token) {
      return toast.error("Page ID y Page Access Token son obligatorios");
    }
    await api.post("/settings/meta-social/config", metaSocialForm);
    toast.success("Meta Social guardado");
    setMetaSocialOpen(false);
    setMetaSocialForm({ page_id: "", page_access_token: "", ig_user_id: "" });
    load();
  };
  const testMetaSocial = async () => {
    try {
      const r = await api.post("/settings/meta-social/test");
      if (r.data.ok) {
        toast.success(`Conectado a página: ${r.data.page_name}${r.data.ig_user_id_detected ? ` + IG: ${r.data.ig_user_id_detected}` : ""}`);
        load();
      } else {
        toast.error(`Error: ${r.data.error}`);
      }
    } catch {
      toast.error("Error testeando conexión Meta");
    }
  };
  const disconnectMetaSocial = async () => {
    await api.post("/settings/meta-social/disconnect");
    toast.success("Meta Social desconectado");
    load();
  };

  // ---- Meta Ads ----
  const saveMetaAds = async () => {
    if (!metaAdsForm.access_token || !metaAdsForm.ad_account_id) {
      return toast.error("Access Token y Ad Account ID son obligatorios");
    }
    await api.post("/settings/meta-ads/config", metaAdsForm);
    toast.success("Meta Ads guardado");
    setMetaAdsOpen(false);
    setMetaAdsForm({ app_id: "", app_secret: "", access_token: "", ad_account_id: "", business_id: "" });
    load();
  };
  const disconnectMetaAds = async () => {
    await api.post("/settings/meta-ads/disconnect");
    toast.success("Meta Ads desconectado");
    load();
  };

  // ---- Google Ads ----
  const saveGoogleAds = async () => {
    if (!googleAdsForm.developer_token || !googleAdsForm.customer_id) {
      return toast.error("Developer Token y Customer ID son obligatorios");
    }
    await api.post("/settings/google-ads/config", googleAdsForm);
    toast.success("Google Ads guardado");
    setGoogleAdsOpen(false);
    setGoogleAdsForm({ developer_token: "", client_id: "", client_secret: "", refresh_token: "", customer_id: "", login_customer_id: "" });
    load();
  };
  const disconnectGoogleAds = async () => {
    await api.post("/settings/google-ads/disconnect");
    toast.success("Google Ads desconectado");
    load();
  };

  return (
    <div className="stagger">
      <PageHeader
        overline="Configuración"
        title="Integraciones y remitentes."
        subtitle="Conecta tus servicios externos: email, calendario, redes sociales y plataformas publicitarias."
      />

      <Tabs defaultValue="senders" className="space-y-6">
        <TabsList className="bg-[#F9F8F6] border border-[#E2DCD0] rounded-sm flex-wrap h-auto">
          <TabsTrigger value="senders" className="rounded-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="settings-tab-senders">Remitentes</TabsTrigger>
          <TabsTrigger value="google" className="rounded-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="settings-tab-google">Google Calendar</TabsTrigger>
          <TabsTrigger value="meta-social" className="rounded-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="settings-tab-meta-social">Meta Social</TabsTrigger>
          <TabsTrigger value="meta-ads" className="rounded-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="settings-tab-meta-ads">Meta Ads API</TabsTrigger>
          <TabsTrigger value="google-ads" className="rounded-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="settings-tab-google-ads">Google Ads API</TabsTrigger>
          <TabsTrigger value="widget" className="rounded-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="settings-tab-widget">Widget Web</TabsTrigger>
          <TabsTrigger value="mailchimp" className="rounded-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="settings-tab-mailchimp">Mailchimp</TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="users" className="rounded-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="settings-tab-users">Usuarios</TabsTrigger>
          )}
        </TabsList>

        {/* SENDERS */}
        <TabsContent value="senders" className="grid lg:grid-cols-2 gap-6">
          <Card className="edit-card p-6 rounded-sm shadow-none">
            <h3 className="font-display text-2xl text-[#0A1128] mb-1">Añadir remitente</h3>
            <p className="text-xs text-[#8A8F98] mb-4">El dominio del email debe estar verificado en Resend (Dashboard → Domains).</p>
            <div className="space-y-3">
              <div><Label>Nombre visible</Label><Input data-testid="sender-name" value={senderForm.name} onChange={(e) => setSenderForm({ ...senderForm, name: e.target.value })} placeholder="Karin · Espíritu Travel" className="mt-1 rounded-sm" /></div>
              <div><Label>Email remitente</Label><Input data-testid="sender-email" type="email" value={senderForm.email} onChange={(e) => setSenderForm({ ...senderForm, email: e.target.value })} placeholder="karin@espiritutravel.com" className="mt-1 rounded-sm" /></div>
              <div><Label>Reply-to (opcional)</Label><Input type="email" value={senderForm.reply_to} onChange={(e) => setSenderForm({ ...senderForm, reply_to: e.target.value })} className="mt-1 rounded-sm" /></div>
              <label className="flex items-center gap-2 text-sm text-[#5C616A]">
                <input type="checkbox" checked={senderForm.is_default} onChange={(e) => setSenderForm({ ...senderForm, is_default: e.target.checked })} />
                Marcar como remitente por defecto
              </label>
              <Button data-testid="sender-add" onClick={addSender} className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm">
                <Plus className="w-4 h-4 mr-2" />Añadir
              </Button>
            </div>
          </Card>

          <Card className="edit-card p-6 rounded-sm shadow-none">
            <h3 className="font-display text-2xl text-[#0A1128] mb-4">Remitentes configurados</h3>
            {senders.length === 0 ? (
              <p className="text-sm text-[#5C616A]">Aún no hay remitentes.</p>
            ) : (
              <ul className="divide-y divide-[#E2DCD0]">
                {senders.map((s) => (
                  <li key={s.id} className="py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-[#0A1128] truncate">{s.name}</p>
                        {s.is_default && <Badge variant="outline" className="rounded-sm text-[10px] border-[#D95D39] text-[#D95D39]">Por defecto</Badge>}
                      </div>
                      <p className="text-sm text-[#5C616A] truncate">{s.email}</p>
                    </div>
                    <div className="flex gap-1">
                      {!s.is_default && (
                        <Button size="sm" variant="outline" onClick={() => setDefault(s)} className="rounded-sm border-[#E2DCD0]">Por defecto</Button>
                      )}
                      <Button size="icon" variant="outline" onClick={() => delSender(s.id)} className="rounded-sm border-[#E2DCD0] hover:border-[#9D0208] hover:text-[#9D0208]">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </TabsContent>

        {/* GOOGLE CALENDAR */}
        <TabsContent value="google" className="grid lg:grid-cols-3 gap-6">
          <Card className="edit-card p-6 rounded-sm shadow-none lg:col-span-2">
            <h3 className="font-display text-2xl text-[#0A1128] mb-1">Conectar Google Calendar</h3>
            <p className="text-xs text-[#8A8F98] mb-5">Cuando agendes reuniones desde el módulo Calendario, se crearán automáticamente en tu Google Calendar e invitarán al lead.</p>

            {gstatus && (
              <div className="mb-5 p-4 rounded-sm border bg-[#F9F8F6]" style={{ borderColor: gstatus.connected ? "#2D6A4F" : "#E2DCD0" }}>
                {gstatus.connected ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex gap-3">
                      <Check className="w-5 h-5 text-[#2D6A4F] mt-0.5" />
                      <div>
                        <p className="font-medium text-[#0A1128]">Conectado a {gstatus.google_email}</p>
                        <p className="text-xs text-[#5C616A] mt-1">Las reuniones se crearán en este calendario.</p>
                      </div>
                    </div>
                    <Button onClick={disconnectGoogle} variant="outline" className="rounded-sm border-[#E2DCD0] hover:border-[#9D0208] hover:text-[#9D0208]" data-testid="google-disconnect">
                      <X className="w-4 h-4 mr-1" />Desconectar
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex gap-3">
                      <AlertCircle className="w-5 h-5 text-[#D95D39] mt-0.5" />
                      <div>
                        <p className="font-medium text-[#0A1128]">{gstatus.configured ? "Configuración guardada" : "Configura credenciales primero"}</p>
                        <p className="text-xs text-[#5C616A] mt-1">{gstatus.configured ? "Haz clic en Conectar para autorizar el acceso a tu calendario." : "Pega tu CLIENT_ID y CLIENT_SECRET de Google Cloud Console."}</p>
                      </div>
                    </div>
                    {gstatus.configured && (
                      <Button onClick={connectGoogle} className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm" data-testid="google-connect">
                        <CalIcon className="w-4 h-4 mr-2" />Conectar
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            <Dialog open={gOpen} onOpenChange={setGOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm border-[#E2DCD0]" data-testid="google-config-button">
                  {gstatus?.configured ? "Editar credenciales" : "Configurar credenciales"}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white rounded-sm max-w-lg">
                <DialogHeader><DialogTitle className="font-display">Credenciales OAuth Google</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Client ID</Label><Input data-testid="google-client-id" value={gcfg.client_id} onChange={(e) => setGcfg({ ...gcfg, client_id: e.target.value })} placeholder="xxxxxxxx-xxx.apps.googleusercontent.com" className="mt-1 rounded-sm font-mono text-xs" /></div>
                  <div><Label>Client Secret</Label><Input data-testid="google-client-secret" value={gcfg.client_secret} onChange={(e) => setGcfg({ ...gcfg, client_secret: e.target.value })} placeholder="GOCSPX-..." className="mt-1 rounded-sm font-mono text-xs" /></div>
                  <div><Label>Redirect URI</Label><Input value={gcfg.redirect_uri} onChange={(e) => setGcfg({ ...gcfg, redirect_uri: e.target.value })} className="mt-1 rounded-sm font-mono text-xs" /></div>
                </div>
                <DialogFooter>
                  <Button data-testid="google-save-config" onClick={saveGoogleConfig} className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm">Guardar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </Card>

          <Card className="edit-card p-6 rounded-sm shadow-none">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98] mb-3">Guía paso a paso</p>
            <ol className="text-sm text-[#1A1F2B] space-y-3">
              <li><strong>1.</strong> Entra en <a className="text-[#D95D39] hover:underline" href="https://console.cloud.google.com" target="_blank" rel="noreferrer">Google Cloud Console</a>.</li>
              <li><strong>2.</strong> APIs & Services → Library → busca <em>Google Calendar API</em> → Enable.</li>
              <li><strong>3.</strong> OAuth consent screen → External → rellena nombre y email.</li>
              <li><strong>4.</strong> Credentials → Create OAuth client ID → Web application.</li>
              <li><strong>5.</strong> Authorized redirect URIs → añade la URI mostrada.</li>
              <li><strong>6.</strong> Copia Client ID y Secret y pégalos arriba.</li>
            </ol>
          </Card>
        </TabsContent>

        {/* META SOCIAL */}
        <TabsContent value="meta-social" className="grid lg:grid-cols-3 gap-6">
          <Card className="edit-card p-6 rounded-sm shadow-none lg:col-span-2">
            <h3 className="font-display text-2xl text-[#0A1128] mb-1 flex items-center gap-2">
              <Facebook className="w-5 h-5" /> <Instagram className="w-5 h-5" /> Meta Social
            </h3>
            <p className="text-xs text-[#8A8F98] mb-5">
              Publicación automática en tu Facebook Page e Instagram Business cada vez que publiques un artículo SEO.
            </p>

            <div className="mb-5 p-4 rounded-sm border bg-[#F9F8F6]" style={{ borderColor: metaSocial.configured ? "#2D6A4F" : "#E2DCD0" }}>
              {metaSocial.configured ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex gap-3">
                    <Check className="w-5 h-5 text-[#2D6A4F] mt-0.5" />
                    <div>
                      <p className="font-medium text-[#0A1128]">Configurado</p>
                      <p className="text-xs text-[#5C616A] mt-1 font-mono">Page ID: {metaSocial.page_id} · Token: {metaSocial.page_access_token_masked}{metaSocial.ig_user_id ? ` · IG: ${metaSocial.ig_user_id}` : " · IG no detectado"}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={testMetaSocial} variant="outline" className="rounded-sm border-[#E2DCD0]" data-testid="meta-social-test">Probar</Button>
                    <Button onClick={disconnectMetaSocial} variant="outline" className="rounded-sm border-[#E2DCD0] hover:border-[#9D0208] hover:text-[#9D0208]" data-testid="meta-social-disconnect">
                      <X className="w-4 h-4 mr-1" />Desconectar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-[#D95D39] mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0A1128]">No configurado</p>
                    <p className="text-xs text-[#5C616A] mt-1">Crea tu app de Meta y pega los datos abajo.</p>
                  </div>
                </div>
              )}
            </div>

            <Dialog open={metaSocialOpen} onOpenChange={setMetaSocialOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm border-[#E2DCD0]" data-testid="meta-social-config-btn">
                  {metaSocial.configured ? "Editar credenciales" : "Configurar credenciales"}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white rounded-sm max-w-lg">
                <DialogHeader><DialogTitle className="font-display">Credenciales Meta Social</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Facebook Page ID</Label><Input data-testid="meta-page-id" value={metaSocialForm.page_id} onChange={(e) => setMetaSocialForm({ ...metaSocialForm, page_id: e.target.value })} placeholder="123456789012345" className="mt-1 rounded-sm font-mono text-xs" /></div>
                  <div><Label>Page Access Token (long-lived)</Label><Input data-testid="meta-page-token" value={metaSocialForm.page_access_token} onChange={(e) => setMetaSocialForm({ ...metaSocialForm, page_access_token: e.target.value })} placeholder="EAAxxxxxxxxxxx..." className="mt-1 rounded-sm font-mono text-xs" /></div>
                  <div><Label>Instagram Business User ID (opcional, se autodetecta)</Label><Input value={metaSocialForm.ig_user_id} onChange={(e) => setMetaSocialForm({ ...metaSocialForm, ig_user_id: e.target.value })} placeholder="17841...." className="mt-1 rounded-sm font-mono text-xs" /></div>
                </div>
                <DialogFooter>
                  <Button data-testid="meta-social-save" onClick={saveMetaSocial} className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm">Guardar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </Card>

          <Card className="edit-card p-6 rounded-sm shadow-none">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98] mb-3">Cómo obtener los datos</p>
            <ol className="text-sm text-[#1A1F2B] space-y-3">
              <li><strong>1.</strong> Ve a <a className="text-[#D95D39] hover:underline" href="https://developers.facebook.com" target="_blank" rel="noreferrer">developers.facebook.com</a> → My Apps → Create App → Business.</li>
              <li><strong>2.</strong> Settings → Basic: pon nombre, política privacidad.</li>
              <li><strong>3.</strong> Add Product → <em>Facebook Login for Business</em> y <em>Instagram Graph API</em>.</li>
              <li><strong>4.</strong> Convierte tu cuenta Instagram a Business y vincúlala a tu Page de Facebook (desde la Page → Settings → Linked Accounts).</li>
              <li><strong>5.</strong> Tools → <a className="text-[#D95D39] hover:underline" href="https://developers.facebook.com/tools/explorer" target="_blank" rel="noreferrer">Graph API Explorer</a> → selecciona tu app → permisos: <code className="text-[10px]">pages_show_list, pages_manage_posts, pages_read_engagement, pages_manage_engagement, instagram_basic, instagram_content_publish</code>.</li>
              <li><strong>6.</strong> Genera User Access Token y luego haz <code className="text-[10px]">GET /me/accounts</code> — copia el <em>access_token</em> y <em>id</em> de tu página.</li>
              <li><strong>7.</strong> Para token long-lived: <code className="text-[10px] break-all bg-[#F9F8F6] p-1 border border-[#E2DCD0] rounded-sm block mt-1">GET /oauth/access_token?grant_type=fb_exchange_token&client_id=APP_ID&client_secret=APP_SECRET&fb_exchange_token=USER_TOKEN</code></li>
              <li><strong>8.</strong> Después <code className="text-[10px]">GET /me/accounts</code> con el nuevo token. El <em>access_token</em> de tu Page será long-lived (sin caducidad).</li>
              <li><strong>9.</strong> App Review: solicita <em>instagram_content_publish</em> para producción.</li>
            </ol>
          </Card>
        </TabsContent>

        {/* META ADS */}
        <TabsContent value="meta-ads" className="grid lg:grid-cols-3 gap-6">
          <Card className="edit-card p-6 rounded-sm shadow-none lg:col-span-2">
            <h3 className="font-display text-2xl text-[#0A1128] mb-1 flex items-center gap-2">
              <Target className="w-5 h-5" /> Meta Ads API
            </h3>
            <p className="text-xs text-[#8A8F98] mb-5">
              Publicación directa de campañas en Meta Ads (Facebook + Instagram). Mientras no tengas tokens, la infraestructura está lista en modo MOCKED.
            </p>

            <div className="mb-5 p-4 rounded-sm border bg-[#F9F8F6]" style={{ borderColor: metaAds.configured ? "#2D6A4F" : "#E2DCD0" }}>
              {metaAds.configured ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex gap-3">
                    <Check className="w-5 h-5 text-[#2D6A4F] mt-0.5" />
                    <div>
                      <p className="font-medium text-[#0A1128]">Conectado</p>
                      <p className="text-xs text-[#5C616A] mt-1 font-mono">Ad Account: act_{metaAds.ad_account_id} · App ID: {metaAds.app_id}</p>
                    </div>
                  </div>
                  <Button onClick={disconnectMetaAds} variant="outline" className="rounded-sm border-[#E2DCD0] hover:border-[#9D0208] hover:text-[#9D0208]" data-testid="meta-ads-disconnect">
                    <X className="w-4 h-4 mr-1" />Desconectar
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-[#D95D39] mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0A1128]">MOCKED — listo para conectar</p>
                    <p className="text-xs text-[#5C616A] mt-1">Las campañas se publican en modo simulado. Pega tus credenciales cuando tengas la App aprobada para publicación real.</p>
                  </div>
                </div>
              )}
            </div>

            <Dialog open={metaAdsOpen} onOpenChange={setMetaAdsOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm border-[#E2DCD0]" data-testid="meta-ads-config-btn">
                  {metaAds.configured ? "Editar credenciales" : "Configurar credenciales"}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white rounded-sm max-w-lg">
                <DialogHeader><DialogTitle className="font-display">Credenciales Meta Ads API</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>App ID</Label><Input value={metaAdsForm.app_id} onChange={(e) => setMetaAdsForm({ ...metaAdsForm, app_id: e.target.value })} className="mt-1 rounded-sm font-mono text-xs" /></div>
                  <div><Label>App Secret</Label><Input value={metaAdsForm.app_secret} onChange={(e) => setMetaAdsForm({ ...metaAdsForm, app_secret: e.target.value })} className="mt-1 rounded-sm font-mono text-xs" /></div>
                  <div><Label>Access Token (ads_management)</Label><Input data-testid="meta-ads-token" value={metaAdsForm.access_token} onChange={(e) => setMetaAdsForm({ ...metaAdsForm, access_token: e.target.value })} className="mt-1 rounded-sm font-mono text-xs" /></div>
                  <div><Label>Ad Account ID (sin "act_")</Label><Input data-testid="meta-ads-account" value={metaAdsForm.ad_account_id} onChange={(e) => setMetaAdsForm({ ...metaAdsForm, ad_account_id: e.target.value })} placeholder="1234567890" className="mt-1 rounded-sm font-mono text-xs" /></div>
                  <div><Label>Business ID (opcional)</Label><Input value={metaAdsForm.business_id} onChange={(e) => setMetaAdsForm({ ...metaAdsForm, business_id: e.target.value })} className="mt-1 rounded-sm font-mono text-xs" /></div>
                </div>
                <DialogFooter>
                  <Button data-testid="meta-ads-save" onClick={saveMetaAds} className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm">Guardar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </Card>

          <Card className="edit-card p-6 rounded-sm shadow-none">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98] mb-3">Cómo obtener acceso</p>
            <ol className="text-sm text-[#1A1F2B] space-y-3">
              <li><strong>1.</strong> Misma app de Meta Social (developers.facebook.com → My Apps).</li>
              <li><strong>2.</strong> Add Product → <em>Marketing API</em>.</li>
              <li><strong>3.</strong> App Review → solicita el permiso <code className="text-[10px]">ads_management</code> (justifica que gestionas campañas propias de Espíritu Travel).</li>
              <li><strong>4.</strong> Crea un <em>System User</em> en <a className="text-[#D95D39] hover:underline" href="https://business.facebook.com/settings/system-users" target="_blank" rel="noreferrer">Business Settings</a> → asigna la cuenta de anuncios → genera token con scope <code className="text-[10px]">ads_management</code> (never expires).</li>
              <li><strong>5.</strong> Copia el <em>Ad Account ID</em> de <a className="text-[#D95D39] hover:underline" href="https://business.facebook.com/adsmanager" target="_blank" rel="noreferrer">Ads Manager</a> (top-left, sin el prefijo "act_").</li>
              <li><strong>6.</strong> Pega aquí Token + Ad Account ID. ¡Listo!</li>
              <li className="text-[#8A8F98] text-xs">⚠️ App Review puede tardar 2–5 días.</li>
            </ol>
          </Card>
        </TabsContent>

        {/* GOOGLE ADS */}
        <TabsContent value="google-ads" className="grid lg:grid-cols-3 gap-6">
          <Card className="edit-card p-6 rounded-sm shadow-none lg:col-span-2">
            <h3 className="font-display text-2xl text-[#0A1128] mb-1 flex items-center gap-2">
              <BadgeDollarSign className="w-5 h-5" /> Google Ads API
            </h3>
            <p className="text-xs text-[#8A8F98] mb-5">
              Publicación directa de Search Campaigns en Google Ads. Mientras no tengas Developer Token aprobado, modo MOCKED.
            </p>

            <div className="mb-5 p-4 rounded-sm border bg-[#F9F8F6]" style={{ borderColor: googleAds.configured ? "#2D6A4F" : "#E2DCD0" }}>
              {googleAds.configured ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex gap-3">
                    <Check className="w-5 h-5 text-[#2D6A4F] mt-0.5" />
                    <div>
                      <p className="font-medium text-[#0A1128]">Conectado</p>
                      <p className="text-xs text-[#5C616A] mt-1 font-mono">Customer: {googleAds.customer_id} · Token: {googleAds.developer_token_masked}</p>
                    </div>
                  </div>
                  <Button onClick={disconnectGoogleAds} variant="outline" className="rounded-sm border-[#E2DCD0] hover:border-[#9D0208] hover:text-[#9D0208]" data-testid="google-ads-disconnect">
                    <X className="w-4 h-4 mr-1" />Desconectar
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-[#D95D39] mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0A1128]">MOCKED — listo para conectar</p>
                    <p className="text-xs text-[#5C616A] mt-1">Las campañas se publican en modo simulado.</p>
                  </div>
                </div>
              )}
            </div>

            <Dialog open={googleAdsOpen} onOpenChange={setGoogleAdsOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm border-[#E2DCD0]" data-testid="google-ads-config-btn">
                  {googleAds.configured ? "Editar credenciales" : "Configurar credenciales"}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white rounded-sm max-w-lg">
                <DialogHeader><DialogTitle className="font-display">Credenciales Google Ads API</DialogTitle></DialogHeader>
                <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                  <div><Label>Developer Token</Label><Input data-testid="google-ads-token" value={googleAdsForm.developer_token} onChange={(e) => setGoogleAdsForm({ ...googleAdsForm, developer_token: e.target.value })} placeholder="abc1XyZ_DEFghi-jkLmno" className="mt-1 rounded-sm font-mono text-xs" /></div>
                  <div><Label>OAuth Client ID</Label><Input value={googleAdsForm.client_id} onChange={(e) => setGoogleAdsForm({ ...googleAdsForm, client_id: e.target.value })} className="mt-1 rounded-sm font-mono text-xs" /></div>
                  <div><Label>OAuth Client Secret</Label><Input value={googleAdsForm.client_secret} onChange={(e) => setGoogleAdsForm({ ...googleAdsForm, client_secret: e.target.value })} className="mt-1 rounded-sm font-mono text-xs" /></div>
                  <div><Label>Refresh Token</Label><Input value={googleAdsForm.refresh_token} onChange={(e) => setGoogleAdsForm({ ...googleAdsForm, refresh_token: e.target.value })} className="mt-1 rounded-sm font-mono text-xs" /></div>
                  <div><Label>Customer ID (10 dígitos)</Label><Input data-testid="google-ads-customer" value={googleAdsForm.customer_id} onChange={(e) => setGoogleAdsForm({ ...googleAdsForm, customer_id: e.target.value })} placeholder="123-456-7890" className="mt-1 rounded-sm font-mono text-xs" /></div>
                  <div><Label>Login Customer ID (Manager MCC, opcional)</Label><Input value={googleAdsForm.login_customer_id} onChange={(e) => setGoogleAdsForm({ ...googleAdsForm, login_customer_id: e.target.value })} className="mt-1 rounded-sm font-mono text-xs" /></div>
                </div>
                <DialogFooter>
                  <Button data-testid="google-ads-save" onClick={saveGoogleAds} className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm">Guardar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </Card>

          <Card className="edit-card p-6 rounded-sm shadow-none">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98] mb-3">Cómo obtener acceso</p>
            <ol className="text-sm text-[#1A1F2B] space-y-3">
              <li><strong>1.</strong> Crea (o usa) una <em>Manager Account</em> de Google Ads en <a className="text-[#D95D39] hover:underline" href="https://ads.google.com/intl/es/home/tools/manager-accounts/" target="_blank" rel="noreferrer">ads.google.com/manager</a>.</li>
              <li><strong>2.</strong> En esa Manager Account: Tools & Settings → API Center.</li>
              <li><strong>3.</strong> Solicita acceso a la API: pon datos de empresa, URL real (espiritutravel.com), email monitorizado. Aprobación: 1–7 días.</li>
              <li><strong>4.</strong> Una vez aprobado, copia el <em>Developer Token</em>.</li>
              <li><strong>5.</strong> En <a className="text-[#D95D39] hover:underline" href="https://console.cloud.google.com" target="_blank" rel="noreferrer">Google Cloud Console</a>: crea proyecto, habilita <em>Google Ads API</em>, crea OAuth Client ID tipo Desktop.</li>
              <li><strong>6.</strong> Genera el Refresh Token siguiendo <a className="text-[#D95D39] hover:underline" href="https://developers.google.com/google-ads/api/docs/oauth/cloud-project" target="_blank" rel="noreferrer">esta guía</a> (script <code className="text-[10px]">generate_user_credentials.py</code>).</li>
              <li><strong>7.</strong> Customer ID: en tu cuenta de anuncios, top-right (formato 123-456-7890, pega sin guiones).</li>
            </ol>
          </Card>
        </TabsContent>

        {/* WIDGET WEB */}
        <TabsContent value="widget" className="grid lg:grid-cols-3 gap-6">
          <Card className="edit-card p-6 rounded-sm shadow-none lg:col-span-2 space-y-5">
            <div>
              <h3 className="font-display text-2xl text-[#0A1128] mb-1 flex items-center gap-2">
                <Code className="w-5 h-5" /> Widget para tu web
              </h3>
              <p className="text-xs text-[#8A8F98]">
                Reemplaza tu botón actual "Plan your trip" por este snippet. Las fotos de tus travel agents rotan y al clic se abre un chat conversacional que cualifica leads y los crea en tu CRM.
              </p>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98] mb-2">Snippet para WordPress / cualquier web</p>
              <div className="relative">
                <pre className="text-[11px] font-mono bg-[#0A1128] text-[#E2DCD0] p-4 rounded-sm overflow-x-auto whitespace-pre">{embedSnippet}</pre>
                <Button size="sm" onClick={copyEmbed} className="absolute top-2 right-2 bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm h-7" data-testid="copy-embed-btn">
                  <Copy className="w-3 h-3 mr-1" />Copiar
                </Button>
              </div>
              <p className="text-[11px] text-[#8A8F98] mt-2">
                Pega el snippet en un bloque HTML personalizado de WordPress donde quieras que aparezca el botón. Si omites el <code className="bg-[#F9F8F6] px-1">&lt;div&gt;</code>, el widget aparece flotando abajo-derecha.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 pt-4 border-t border-[#E2DCD0]">
              <div>
                <Label className="text-xs">Texto del botón</Label>
                <Input data-testid="widget-cta" value={widgetForm.cta_label} onChange={(e) => setWidgetForm({ ...widgetForm, cta_label: e.target.value })} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs">Color primario</Label>
                <Input type="color" value={widgetForm.primary_color} onChange={(e) => setWidgetForm({ ...widgetForm, primary_color: e.target.value })} className="mt-1 rounded-sm h-9 p-1" />
              </div>
              <div>
                <Label className="text-xs">Color oscuro</Label>
                <Input type="color" value={widgetForm.dark_color} onChange={(e) => setWidgetForm({ ...widgetForm, dark_color: e.target.value })} className="mt-1 rounded-sm h-9 p-1" />
              </div>
            </div>
            <Button onClick={saveWidget} data-testid="widget-save" className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm">Guardar diseño</Button>

            <div className="pt-5 border-t border-[#E2DCD0]">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98] mb-3">Travel agents (las fotos rotan en el widget)</p>
              {widget?.agents?.length > 0 && (
                <ul className="space-y-2 mb-4">
                  {widget.agents.map((a) => (
                    <li key={a.id} className="flex items-center gap-3 p-2 border border-[#E2DCD0] rounded-sm">
                      <img src={a.photo_url} alt={a.name} className="w-12 h-12 rounded-sm object-cover border-2 border-[#D95D39]" />
                      <div className="flex-1">
                        <p className="font-medium text-sm text-[#0A1128]">{a.name}</p>
                        <p className="text-[10px] text-[#8A8F98] font-mono truncate">{a.photo_url.slice(0, 60)}{a.photo_url.length > 60 ? "..." : ""}</p>
                      </div>
                      <Button size="icon" variant="outline" onClick={() => delAgent(a.id)} className="rounded-sm border-[#E2DCD0] hover:border-[#9D0208] hover:text-[#9D0208]">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="grid sm:grid-cols-2 gap-2">
                <Input placeholder="Nombre (Karin, Lucia, María...)" value={newAgent.name} onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })} className="rounded-sm" />
                <Input placeholder="URL foto (https://...)" value={newAgent.photo_url} onChange={(e) => setNewAgent({ ...newAgent, photo_url: e.target.value })} className="rounded-sm font-mono text-xs" />
              </div>
              <Button onClick={addAgent} variant="outline" size="sm" className="mt-2 rounded-sm border-[#E2DCD0]" data-testid="widget-add-agent">
                <Plus className="w-3 h-3 mr-1" />Añadir travel agent
              </Button>
            </div>
          </Card>

          <Card className="edit-card p-6 rounded-sm shadow-none">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98] mb-3">Cómo instalar</p>
            <ol className="text-sm text-[#1A1F2B] space-y-3">
              <li><strong>1.</strong> WordPress → Pages → Edita la home de Espíritu Travel.</li>
              <li><strong>2.</strong> Localiza el bloque actual "Plan your trip" y elimínalo (o reemplaza el bloque HTML).</li>
              <li><strong>3.</strong> Inserta un bloque "HTML personalizado" / "Code block".</li>
              <li><strong>4.</strong> Pega el snippet de la izquierda.</li>
              <li><strong>5.</strong> Guarda y publica. Las fotos rotarán cada 3.5s y al clic se abrirá Maya en un modal.</li>
              <li className="text-[#8A8F98] text-xs pt-2">💡 Los leads se crean automáticamente en tu CRM al capturar el email del visitante.</li>
            </ol>
            <div className="mt-5 pt-5 border-t border-[#E2DCD0]">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98] mb-2">Vista previa</p>
              <a href="/widget-preview.html" target="_blank" rel="noreferrer" className="text-xs text-[#D95D39] hover:underline inline-flex items-center gap-1">
                Abrir vista previa <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </Card>
        </TabsContent>

        {/* MAILCHIMP */}
        <TabsContent value="mailchimp" className="space-y-6">
          <Card className="edit-card p-6 rounded-sm shadow-none">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="font-display text-2xl text-[#0A1128] mb-1">Mailchimp</h3>
                <p className="text-xs text-[#8A8F98]">
                  Importa contactos existentes y sincroniza nuevos leads automáticamente con tu audiencia.
                </p>
              </div>
              <Badge variant="outline" className={`rounded-sm text-[10px] ${mailchimp.configured ? "border-[#2D6A4F] text-[#2D6A4F] bg-[#2D6A4F]/5" : "border-[#E2DCD0] text-[#8A8F98]"}`}>
                {mailchimp.configured ? "Conectado" : "No configurado"}
              </Badge>
            </div>

            {!mailchimp.configured && (
              <div className="bg-[#FEF6E5] border border-[#EAB308]/40 rounded-sm p-3 text-xs text-[#5C616A] mb-4 inline-flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-[#EAB308] shrink-0 mt-0.5" />
                Falta configurar la API key (variable MAILCHIMP_API_KEY).
              </div>
            )}

            {mailchimp.configured && (
              <div className="space-y-4">
                <div>
                  <Label>Audiencia activa</Label>
                  <p className="text-xs text-[#8A8F98] mb-2">Esta audiencia recibirá los leads nuevos automáticamente y será el origen del import inicial.</p>
                  <select
                    data-testid="mailchimp-audience-select"
                    value={mailchimp.list_id || ""}
                    onChange={async (e) => {
                      const list_id = e.target.value;
                      try {
                        await api.post("/mailchimp/settings", { list_id, auto_sync: true, extra_default_tags: mailchimp.extra_default_tags || [] });
                        toast.success("Audiencia guardada");
                        setMailchimp({ ...mailchimp, list_id });
                      } catch {
                        toast.error("No se pudo guardar");
                      }
                    }}
                    className="w-full bg-white border border-[#E2DCD0] rounded-sm px-3 py-2 text-sm text-[#0A1128] focus:outline-none focus:border-[#0A1128]"
                  >
                    <option value="">— Selecciona una audiencia —</option>
                    {mailchimpAudiences.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} ({a.member_count.toLocaleString()} miembros)
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Tag automático para cada lead</Label>
                  <p className="text-xs text-[#8A8F98] mb-2">
                    Se añadirá a TODOS los nuevos leads que sincronicemos a Mailchimp. Útil para aislarlos dentro de audiencias grandes (ej: <code className="bg-[#F2EFE6] px-1">voyagemarkt-2026</code>, <code className="bg-[#F2EFE6] px-1">web-leads</code>).
                  </p>
                  <div className="flex gap-2">
                    <Input
                      data-testid="mailchimp-extra-tag"
                      value={(mailchimp.extra_default_tags || []).join(", ")}
                      onChange={(e) => setMailchimp({ ...mailchimp, extra_default_tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                      placeholder="voyagemarkt-2026"
                      className="rounded-sm border-[#E2DCD0]"
                    />
                    <Button
                      onClick={async () => {
                        try {
                          await api.post("/mailchimp/settings", {
                            list_id: mailchimp.list_id || null,
                            auto_sync: true,
                            extra_default_tags: mailchimp.extra_default_tags || [],
                          });
                          toast.success("Tag guardado");
                        } catch { toast.error("No se pudo guardar"); }
                      }}
                      data-testid="mailchimp-save-tags"
                      className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm shrink-0"
                    >
                      Guardar
                    </Button>
                  </div>
                </div>

                <div className="pt-4 border-t border-[#E2DCD0]">
                  <p className="text-sm font-medium text-[#0A1128] mb-1">Importación inicial</p>
                  <p className="text-xs text-[#8A8F98] mb-3">
                    Trae todos los contactos de la audiencia seleccionada al CRM como leads.
                    {mailchimp.last_imported_at && (
                      <span> Última importación: {new Date(mailchimp.last_imported_at).toLocaleString()} ({mailchimp.last_import_count} contactos).</span>
                    )}
                  </p>
                  <Button
                    data-testid="mailchimp-import-btn"
                    onClick={async () => {
                      if (!mailchimp.list_id) {
                        toast.error("Selecciona una audiencia primero");
                        return;
                      }
                      if (!window.confirm("¿Importar todos los contactos de esta audiencia al CRM? Los emails que ya existan se actualizan, no se duplican.")) return;
                      setMailchimpImporting(true);
                      try {
                        const r = await api.post("/mailchimp/import");
                        toast.success(`Importados: ${r.data.created} nuevos, ${r.data.updated} actualizados`);
                        load();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Error en la importación");
                      } finally {
                        setMailchimpImporting(false);
                      }
                    }}
                    disabled={!mailchimp.list_id || mailchimpImporting}
                    className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm"
                  >
                    {mailchimpImporting ? "Importando..." : "Importar audiencia al CRM"}
                  </Button>
                </div>

                <div className="pt-4 border-t border-[#E2DCD0] text-xs text-[#8A8F98] space-y-1">
                  <p>• <strong>Sync automático activo</strong>: cada lead nuevo o actualizado se empuja a Mailchimp con tags (destino, score, source, status).</p>
                  <p>• Los tags usan formato <code className="bg-[#F2EFE6] px-1 rounded">score-high</code>, <code className="bg-[#F2EFE6] px-1 rounded">src-chat-publico</code>, etc. para que puedas segmentar fácilmente.</p>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* USERS — only for super_admin */}
        {isSuperAdmin && (
          <TabsContent value="users">
            <UsersTab currentUserId={currentUser?.id} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
